import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { EventType, Roster } from '../../../../../../models/event-type';
import { CallbackResultStatus, dateStringFromISO8601 } from '../../../../../../utils';
import { AccountService } from '../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { EventTypeStatus } from '../../../../../../models/event-type-enums';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SimpleMessageModalComponent } from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';

@Component({
  selector: 'app-event-type-rosters',
  templateUrl: './event-type-rosters.component.html',
  styleUrls: ['./event-type-rosters.component.css']
})
export class EventTypeRostersComponent implements OnInit {
  public eventType: EventType = null;
  public rosters: Roster[];

  public loadingEventType = true;
  public loadingRosters = true;

  public notFound = false;
  public successMessage = '';
  public errorMessage = '';
  public serverError: number = null;

  public dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    const eventTypeIdInt = parseInt(routeParams.id, 10);
    if (isNaN(eventTypeIdInt)) {
      this.notFound = true;
      return;
    }

    this.loadEventType(eventTypeIdInt);
    this.loadRosters(eventTypeIdInt);
  }

  public loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  public loadRosters(eventTypeId: number) {
    this.loadingRosters = true;

    this.eventTypeService
      .getRosters(
        1,
        3000,
        {event_type_id: eventTypeId}
      )
      .then(response => {
        this.rosters = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRosters = false; });
  }

  public handleRemoveRoster(roster: Roster) {
    this.successMessage = '';
    this.errorMessage = '';

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Remove Roster';
    modalRef.componentInstance.messageText = 'Are you sure you want to remove the "' + roster.name + '" roster? Any existing roster memberships will not be affected – they must separately be either cancelled or moved to a different roster.';
    modalRef.componentInstance.messageAlertClass = 'alert-warning';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingRosters = true;

      this.eventTypeService
        .removeRoster(roster)
        .then(() => {
          this.successMessage = 'Roster "' + roster.name + '" removed successfully.';
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errorMessage = reason.error;
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to remove this roster.'
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadRosters(this.eventType.id); })
    }
  }

  protected readonly CallbackResultStatus = CallbackResultStatus;
}
